/**
 * Link a heat to its MTR/CMTR record. CR-2026-QDP (2026-10-14). Server: /api/qdp-links/heats/:id/mtr.
 * A typed heat number is not a link. A wrong link is voided with a reason, never edited or deleted.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Card, MenuItem, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

export default function HeatMtrPanel({ heatId }) {
  const { loginUser } = useSelector((s) => s.userSlice);
  const canEdit = hasPermission(loginUser?.decodedToken?.permissions || [], "edit_heat");
  const [data, setData] = useState(null);
  const [pick, setPick] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    if (!heatId) return;
    try { const r = await ApiCall.get(`/qdp-links/heats/${heatId}/mtr`); setData(r.data.data); } catch (e) { setData(null); setMsg({ severity: "error", text: msgOf(e, "Could not load MTR links.") }); }
  }, [heatId]);
  useEffect(() => { load(); }, [load]);
  const link = async () => {
    const [source, sourceId] = pick.split(":");
    try { const r = await ApiCall.post(`/qdp-links/heats/${heatId}/mtr`, { source, sourceId: Number(sourceId) }); setMsg({ severity: "success", text: r.data.message }); setPick(""); load(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not linked.") }); }
  };
  const voidIt = async (id) => {
    try { const r = await ApiCall.post(`/qdp-links/mtr/${id}/void`, { reason: voidReason }); setMsg({ severity: "success", text: r.data.message }); setVoidReason(""); load(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not voided.") }); }
  };
  if (!data) return msg ? <Alert severity={msg.severity} sx={{ mt: 2 }}>{msg.text}</Alert> : null;
  const active = data.links.filter((l) => !l.voidedAt);
  return (
    <Card sx={{ mt: 3, p: 2 }}>
      <Typography variant="h6">MTR / CMTR record</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>The quality data package needs every heat linked to its mill test report record.</Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 1 }}>{msg.text}</Alert>}
      {active.length === 0 && <Alert severity="warning" sx={{ mb: 1 }}>No MTR linked to heat {data.heat.heatNumber}.</Alert>}
      {data.links.length > 0 && (
        <SmartTable tableId="heat-mtr" lockedColumns={["Record", "Linked", "Status"]} size="small" sx={{ mb: 1 }}>
          <TableHead><TableRow><TableCell>Record</TableCell><TableCell>Linked</TableCell><TableCell>Status</TableCell>{canEdit && <TableCell />}</TableRow></TableHead>
          <TableBody>
            {data.links.map((l) => (
              <TableRow key={l.id} sx={{ opacity: l.voidedAt ? 0.5 : 1 }}>
                <TableCell>{l.name}{l.certNumber ? ` (cert ${l.certNumber})` : ""}</TableCell>
                <TableCell>{l.linkedByName} {String(l.linkedAt || "").slice(0, 10)}</TableCell>
                <TableCell>{l.voidedAt ? `Voided: ${l.voidReason}` : "Active"}</TableCell>
                {canEdit && <TableCell>{!l.voidedAt && <Button size="small" color="error" disabled={voidReason.trim().length < 4} onClick={() => voidIt(l.id)}>Void</Button>}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </SmartTable>
      )}
      {canEdit && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField select size="small" label="MTR record on this job" value={pick} onChange={(e) => setPick(e.target.value)} sx={{ minWidth: 320 }}>
            <MenuItem value="">Choose a record</MenuItem>
            {data.candidates.map((c) => <MenuItem key={`${c.source}:${c.sourceId}`} value={`${c.source}:${c.sourceId}`}>{c.source === "CMTR" ? `CMTR ${c.heatNumber || ""} ${c.certNumber || ""} (${c.status})` : "Document"}: {c.name}</MenuItem>)}
          </TextField>
          <Button variant="contained" disabled={!pick} onClick={link}>Link</Button>
          <TextField size="small" label="Reason to void a link" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
        </Stack>
      )}
    </Card>
  );
}
